import type { PostgrestError } from '@supabase/supabase-js';

/**
 * The typed error every repository surfaces. `code` is stable and maps to a
 * localized message in the UI; raw errors never reach the user
 * (instructions/06_SECURITY.md — no information leakage).
 */
export type AppErrorCode =
  | 'network'
  | 'server'
  | 'not-authenticated'
  | 'not-authorized'
  | 'not-found'
  | 'validation'
  | 'conflict'
  | 'unknown';

export interface AppError {
  readonly code: AppErrorCode;
  readonly message: string;
  readonly cause?: unknown;
}

/** Discriminated result. Repositories return this; UI never sees raw throws. */
export type Result<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: AppError };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });
export const failure = (error: AppError): Result<never> => ({ ok: false, error });

export function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    typeof (value as AppError).code === 'string'
  );
}

/** Throw a typed AppError (used inside repository `run` blocks). */
export function raise(code: AppErrorCode, message: string = code, cause?: unknown): never {
  throw { code, message, cause } satisfies AppError;
}

/** Map a Supabase/PostgREST error to an AppError code. */
export function fromPostgrest(error: PostgrestError): AppError {
  // PostgREST returns 401/403 as RLS denials, 23505 as unique violation, etc.
  const code = error.code;
  if (code === '23505') return { code: 'conflict', message: 'conflict', cause: error };
  if (code === '42501') return { code: 'not-authorized', message: 'not-authorized', cause: error };
  if (code === 'PGRST116') return { code: 'not-found', message: 'not-found', cause: error };
  return { code: 'server', message: 'server', cause: error };
}

/**
 * Runs a data-access function and normalizes any failure into a Result.
 * Thrown AppErrors pass through; anything else becomes a `network` error.
 */
export async function guard<T>(run: () => Promise<T>): Promise<Result<T>> {
  try {
    return ok(await run());
  } catch (error) {
    if (isAppError(error)) return failure(error);
    return failure({ code: 'network', message: 'network', cause: error });
  }
}

/**
 * Adapts a `Result`-returning repository call for React Query, which models
 * errors by rejection. On failure it throws the typed `AppError`.
 */
export async function unwrap<T>(promise: Promise<Result<T>>): Promise<T> {
  const result = await promise;
  if (result.ok) return result.value;
  throw result.error;
}
